/**
 * Para birimleri — BIRIM KUR ile TOPLAM TUTARI TIP DUZEYINDE ayirir.
 *
 * ALAN BILGISI (yanlis anlasilmasi gercek veri karisikligina yol acti):
 *   `offer_price` bir BIRIM KURDUR  — 1 birim dovizin TL fiyati (orn. 34,22).
 *   Islemin TOPLAMI ayri bir sayidir — offerPrice * taskAmount.
 * Ikisi de "para" gibi gorundugu icin biri digerinin yerine gecirilmis ve
 * ekranlarda 34,21 TL'lik islemler gorunmustu.
 *
 * Cozum: ikisi de `number` AMA farkli markali tipler. Bir UnitRate'i
 * TotalAmount bekleyen yere veremezsin — derleme hatasi alirsin. Donusum tek
 * yoldan yapilir: totalOf(rate, taskAmount).
 */

/** 1 birim dovizin fiyati (offer_price). TOPLAM DEGILDIR. */
export type UnitRate = number & { readonly __kind: 'UnitRate' };

/** Islemin toplam tutari (birim kur x miktar). */
export type TotalAmount = number & { readonly __kind: 'TotalAmount' };

/** Sunucudan gelen ham offer_price degerini birim kur olarak isaretler. */
export const unitRate = (value: number): UnitRate => value as UnitRate;

/** Zaten toplam olan bir degeri (orn. hacim raporu) isaretler. */
export const totalAmount = (value: number): TotalAmount => value as TotalAmount;

/**
 * Toplami hesaplamanin TEK yolu. `taskAmount` islem miktaridir (kac birim doviz).
 * Bu fonksiyondan gecmeyen hicbir sey TotalAmount olamaz.
 */
export const totalOf = (rate: UnitRate, taskAmount: number): TotalAmount =>
  (rate * taskAmount) as TotalAmount;

/**
 * PIYASA kurlarinda ondalik — TCMB dovizi dort haneyle yayinliyor ve capraz kurlar da
 * o hassasiyetle hesaplaniyor (lib/rate-mapping.ts). Burada 2'ye dusurmek gercek bir
 * bilgiyi silerdi.
 */
export const RATE_FRACTION_DIGITS = 4;

/**
 * TEKLIF fiyatinda ondalik.
 *
 * 2 — cunku `task_offer.offer_price` kolonu numeric(11,2)
 * (currency-management/resources/migrations/2026-08-29-task-amount-numeric.sql).
 * Sunucu 4 haneli bir teklif fiyatini SAKLAYAMIYOR; 4 hane basmak "34,2150" gibi
 * olmayan bir hassasiyet ima ediyordu — son iki hane her zaman uydurmaydi.
 *
 * Piyasa kuru ile teklif fiyati ikisi de UnitRate; MoneyText aralarindaki farki
 * goremez, o yuzden teklif fiyati basan yerler bunu ACIKCA gecmek zorunda.
 */
export const OFFER_PRICE_FRACTION_DIGITS = 2;

/** Toplamlarda varsayilan ondalik — para iki haneyle konusulur. */
export const TOTAL_FRACTION_DIGITS = 2;

/**
 * tr-TR bicimlendirme: binlik '.', ondalik ','. Intl her cagrida yeni bir
 * bicimlendirici uretmek pahali oldugu icin onbellege alinir (uzun listelerde
 * olculebilir fark yaratir).
 */
const formatterCache = new Map<string, Intl.NumberFormat>();

export const formatMoney = (value: number, fractionDigits: number): string => {
  const key = String(fractionDigits);
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    formatterCache.set(key, formatter);
  }
  return formatter.format(value);
};
